import { useEffect, useMemo, useState } from 'react';
import { captureSupabaseError } from '../lib/supabaseError';

import { axiosRubyClient } from '@/util/axios/axiosClient';

import type { CommsBackend } from '../lib/config';

export interface WorkerChip {
  id: number;
  active: boolean;
  avatar_url: string | null;
  user: {
    id: number;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null;
  // Canary: which comms backend this worker is on. Sourced from the graphy
  // admin `/api/admin/workers/batch` payload (backend team is adding the
  // `comms_backend` field). Optional so the list degrades gracefully to the
  // PubNub default until the field ships.
  comms_backend?: CommsBackend;
}

interface BatchResponse {
  success: boolean;
  data: WorkerChip[];
}

// Module-level cache keyed by worker id. TTL is 5 minutes — long enough
// that repeated mounts of the chat list / broadcast detail don't re-hit
// the batch endpoint for the same ids, short enough that stale name or
// avatar changes self-heal within a session.
const CACHE_TTL_MS = 5 * 60 * 1000;
const BATCH_LIMIT = 200; // matches graphy batch endpoint cap
const chipCache = new Map<number, { chip: WorkerChip; expiresAt: number }>();

function readFromCache(ids: number[]): {
  cached: Record<number, WorkerChip>;
  missing: number[];
} {
  const now = Date.now();
  const cached: Record<number, WorkerChip> = {};
  const missing: number[] = [];
  for (const id of ids) {
    const entry = chipCache.get(id);
    if (entry && entry.expiresAt > now) {
      cached[id] = entry.chip;
    } else {
      missing.push(id);
    }
  }
  return { cached, missing };
}

function writeToCache(chips: WorkerChip[]): void {
  const expiresAt = Date.now() + CACHE_TTL_MS;
  for (const chip of chips) {
    chipCache.set(chip.id, { chip, expiresAt });
  }
}

// Evict specific worker ids from the module-level cache so the next mount of a
// consumer refetches them from graphy. Call after a mutation that changes a
// worker's `comms_backend` (e.g. adding/removing from the Supabase cohort) —
// otherwise the cached chip serves a stale backend badge until the 5-minute
// TTL expires or the page is hard-refreshed.
export function invalidateWorkerChips(ids: number[]): void {
  for (const id of ids) {
    if (Number.isFinite(id) && id > 0) chipCache.delete(id);
  }
}

export function useWorkersByIds(ids: number[]) {
  const [workers, setWorkers] = useState<Record<number, WorkerChip>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dedupedIds = useMemo(() => {
    const set = new Set<number>();
    for (const id of ids) {
      if (Number.isFinite(id) && id > 0) set.add(id);
    }
    return Array.from(set).sort((a, b) => a - b);
  }, [ids]);

  const idsKey = dedupedIds.join(',');

  useEffect(() => {
    if (dedupedIds.length === 0) {
      setWorkers({});
      return;
    }
    const { cached, missing } = readFromCache(dedupedIds);
    // Surface cached chips immediately so the UI renders without
    // waiting for the network round-trip on revisits.
    setWorkers(cached);
    if (missing.length === 0) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    const chunks: number[][] = [];
    for (let i = 0; i < missing.length; i += BATCH_LIMIT) {
      chunks.push(missing.slice(i, i + BATCH_LIMIT));
    }
    Promise.all(
      chunks.map((chunk) =>
        axiosRubyClient.get<BatchResponse>('/api/admin/workers/batch', {
          params: { ids: chunk },
        }),
      ),
    )
      .then((responses) => {
        if (cancelled) return;
        const fetched: WorkerChip[] = [];
        for (const res of responses) {
          for (const w of res.data.data ?? []) fetched.push(w);
        }
        writeToCache(fetched);
        setWorkers((prev) => {
          const next = { ...prev };
          for (const w of fetched) next[w.id] = w;
          return next;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        captureSupabaseError(err, { action: 'fetch_workers_batch' });
        setError(
          (
            err as { response?: { data?: { errors?: string[] } } }
          )?.response?.data?.errors?.join(', ') ??
            (err as Error)?.message ??
            'Failed to load workers',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idsKey, dedupedIds]);

  return { workers, loading, error };
}
